import { useEffect } from "react";
import { DatePicker, TextField, TextFieldController } from "@bonliva-crm/ui";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles
} from "@material-ui/core";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import * as yup from "yup";
import { DevTool } from "@hookform/devtools";
import { useDialog, useLocale } from "@hooks";
import { ArrowDropDown } from "@material-ui/icons";
import {
  CustomerFullSearchModal,
  CustomerFullSearchValue
} from "@customers-new/customer-search/full/customer-full-search-modal";
import { CustomerType } from "@bonliva-crm/shared";
import { implicitToLocal } from "@bonliva-crm/common";
import { ConsultantServicePreferenceFormFragment } from "@bonliva-crm/graphql";
import { useServicePreferencesStore } from "../use-service-preferences-store";

export type FormValues = {
  id?: string;
  description: string;
  period: {
    start: Date;
    end: Date;
  };
  customers: CustomerFullSearchValue[] | null;
};

const initialValues: FormValues = {
  period: null,
  description: "",
  customers: []
};

type Props = {
  loading?: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (values: FormValues, onSuccess?: (data: FormValues) => void) => void;
  servicePrefId?: string;
};

export const ServicePreferenceFormModal = ({
  servicePrefId,
  open,
  onClose,
  onSubmit,
  loading
}: Props) => {
  const customerDialog = useDialog();
  const { servicePreferenceForm } = useServicePreferencesStore();
  const classes = useStyles();
  const { locale } = useLocale();

  const form = useForm<FormValues>({
    defaultValues: initialValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (open && servicePrefId) {
      servicePreferenceForm
        .get({
          variables: {
            id: servicePrefId
          }
        })
        .then((res) => {
          form.reset(transformValues(res.data.consultantServicePreferenceForm));
        });
    }

    if (!open) {
      form.reset(initialValues);
    }
  }, [open, servicePrefId]);

  const handleSubmit = (onSuccess?: (data: any) => void) => {
    onSubmit(form.getValues(), onSuccess);
  };

  const _loading = servicePreferenceForm.loading || loading;
  //! Check if the form is dirty (hack) react-hook-form does not change isDirty to true
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
  const disabled = _loading || !isDirty;
  const canSubmit = form.formState.isValid && !_loading && isDirty;
  const isEditing = !!servicePrefId;

  const _onSubmitClick = () => {
    if (!canSubmit) {
      form.trigger();
    } else {
      handleSubmit();
    }
  };

  const filterOnlySundaysAndMondays = (date: Date, period: FormValues["period"]) => {
    const day = moment(date).day();

    if (period?.start && period?.end) {
      return day === 1;
    }

    if (!period?.start) {
      return day === 1;
    }

    return day === 0;
  };

  return (
    <FormProvider {...form}>
      <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
        {/* DevTool can affect form state validation */}
        <DevTool control={form.control} placement={"top-right"} />
        <DialogTitle>{isEditing ? locale.editForm : locale.newForm}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="period"
                control={form.control}
                render={({ field, fieldState }) => (
                  <DatePicker
                    value={field.value?.start}
                    label={locale.Period}
                    error={!!fieldState.error}
                    required
                    DatePickerProps={{
                      filterDate: (date) =>
                        filterOnlySundaysAndMondays(date, field.value),
                      className: classes.periodPicker,
                      startDate: field.value?.start,
                      endDate: field.value?.end,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      selectsRange: true,
                      selectsDisabledDaysInRange: true,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      onChange: (value: [Date | null, Date | null]) => {
                        field.onChange({
                          start: value[0],
                          end: value[1]
                        });
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextFieldController
                name="description"
                control={form.control}
                label={locale.description}
                textFieldProps={{
                  fullWidth: true,
                  multiline: true,
                  helperText: "",
                  InputLabelProps: {
                    shrink: true
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="customers"
                control={form.control}
                render={({ field }) => {
                  const value = Array.isArray(field.value)
                    ? field.value.map((item) => item.Name).join(", ")
                    : null;

                  return (
                    <Box>
                      <TextField
                        onClick={customerDialog.onOpen}
                        fullWidth
                        value={value}
                        isClearable
                        onClear={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          field.onChange([]);
                        }}
                        FormHelperTextProps={{
                          className: classes.customerInfo
                        }}
                        helperText={!value ? locale.servicePreferences.customerInfo : ""}
                        label={locale.Customer}
                        InputProps={{
                          readOnly: true,
                          multiline: true,
                          endAdornment: <ArrowDropDown className={classes.arrowIcon} />
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                      <CustomerFullSearchModal
                        {...field}
                        open={customerDialog.open}
                        setOpen={customerDialog.onClose}
                        customerTypes={[CustomerType.Hospital, CustomerType.Region]}
                        isMulti
                      />
                    </Box>
                  );
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box className={classes.btnWrap}>
            <Button color={"primary"} className={classes.cancelBtn} onClick={onClose}>
              {locale.Cancel}
            </Button>
            <Button
              color="primary"
              variant={"contained"}
              disabled={disabled}
              onClick={_onSubmitClick}
            >
              {locale.Save}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

const transformValues = (item: ConsultantServicePreferenceFormFragment): FormValues => {
  const { _id, description, period, customers } = item;

  return {
    id: _id,
    description,
    period: {
      start: implicitToLocal(period.start),
      end: implicitToLocal(period.end)
    },
    customers
  };
};

const schema = yup.object().shape({
  description: yup.string().required(),
  period: yup.object().shape({
    start: yup.date().required(),
    end: yup.date().required()
  }),
  customers: yup.array()
});

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    overflow: "hidden",
    minHeight: "300px"
  },
  periodPicker: {
    width: "300px"
  },
  customerInfo: {
    margin: "0 5px",
    fontSize: "0.87rem"
  },
  arrowIcon: {
    marginLeft: "5px"
  },
  btnWrap: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    padding: theme.spacing(2),
    gap: theme.spacing(2)
  },
  cancelBtn: {
    marginRight: "auto"
  }
}));
